import type { KyInstance } from 'ky';

import { DEFAULT_DATE_FORMAT, ensureError, formatDate, ifDefined } from '@enterprise/core';

import { Uri } from '#constants/uri';

import type { GetCalendarDataExportQuery } from './types/get-calendar-data-export-query.type';

/**
 * Fetches a calendar data export as a Blob from the API.
 *
 * Sends a GET request to the calendar export endpoint with optional query parameters
 * for start and end dates, file name, and search string. Dates are formatted using
 * the default date format. Returns the exported data as a Blob.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param query - The query parameters for the export, including startDate, endDate, fileName, and search.
 * @param isExternal - Whether to use the external mailings endpoint. Defaults to `false`.
 * @returns A promise that resolves to a Blob containing the exported calendar data.
 * @throws Throws an error if the request fails.
 */
export const getCalendarDataExport = async (
  instance: KyInstance,
  query: GetCalendarDataExportQuery,
  isExternal = false
) => {
  const { endDate, fileName, search, startDate } = query;

  try {
    return await instance
      .get(isExternal ? Uri.calendar.exportExternal : Uri.calendar.export, {
        searchParams: {
          endDate: ifDefined(endDate, (date) => {
            return formatDate(date, DEFAULT_DATE_FORMAT);
          }),
          fileName,
          search,
          startDate: ifDefined(startDate, (date) => {
            return formatDate(date, DEFAULT_DATE_FORMAT);
          }),
        },
      })
      .blob();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
